<template>
    <div class="localized-date-picker" :class="rootClasses" ref="anchor" :tabindex="disabled ? -1 : 0" @click="onTriggerClick" @keydown="onTriggerKeydown">
        <input
            ref="nativeInput"
            class="localized-date-picker__input"
            type="text"
            :value="nativeInputValue"
            :disabled="disabled"
            :min="nativeMin"
            :max="nativeMax"
            :placeholder="inputPlaceholder"
            :lang="inputLocale"
            :aria-invalid="invalidInput ? 'true' : undefined"
            autocomplete="off"
            @click.stop
            @keydown.stop="onInputKeydown"
            @input="onTextInput"
            @change="onNativeInputChange"
        />
        <span
            v-if="canClear"
            class="localized-date-picker__clear"
            role="button"
            :aria-label="$t('button.delete')"
            tabindex="0"
            @click="onClearClick"
            @keydown.enter.prevent.stop="onClearClick"
            @keydown.space.prevent.stop="onClearClick"
        ></span>

        <div v-if="isOpen" ref="backdrop" class="localized-date-picker__backdrop" @mousedown.stop @click.stop="closeCalendar"></div>
        <div v-if="isOpen" ref="popover" class="localized-date-picker__popover" @mousedown.stop @click.stop>
            <functional-calendar
                :key="inputLocale"
                ref="calendar"
                v-model="calendarValue"
                :is-date-picker="true"
                :date-format="'yyyy-mm-dd'"
                :day-names="dayNames.slice()"
                :month-names="monthNames"
                :short-month-names="monthNames"
                :sunday-start="calendarLocale.sundayStart"
                :change-month-function="true"
                :change-year-function="true"
                :limits="pickerLimits"
                :new-current-date="initialPickerDate"
                @input="onCalendarInput"
            />
            <input
                v-if="mode === 'datetime'"
                type="time"
                class="localized-date-picker__time"
                :value="draftTime"
                @change="onTimeChanged"
                @click.stop
            />
        </div>
    </div>
</template>

<script>
import { dateInputCalendar, dateInputPlaceholder, formatDateInput, isDateInputInRange, parseDateInput, resolveDateDisplayLocale } from '@/utils/dateInput'
import { createNativeLikeChangeEvent, parseCalendarDate, parseNativeDateValue, serializeNativeDateValue } from '@/plugins/localizedCalendar/nativeValue'

const POPOVER_WIDTH = 320
const POPOVER_MIN_HEIGHT = 380
const POPOVER_GAP = 4

export default {
    name: 'LocalizedDatePicker',
    props: {
        value: {
            type: String,
            default: ''
        },
        disabled: {
            type: Boolean,
            default: false
        },
        min: {
            type: String,
            default: ''
        },
        max: {
            type: String,
            default: ''
        },
        mode: {
            type: String,
            default: 'date',
            validator: function (value) {
                return value === 'date' || value === 'datetime'
            }
        },
        placeholder: {
            type: String,
            default: ''
        }
    },
    data() {
        return {
            isOpen: false,
            calendarValue: {},
            lastSelectedIso: '',
            initialPickerDate: new Date(),
            repositionTimer: null,
            draftDate: null,
            draftTime: '',
            inputText: null,
            invalidInput: false
        }
    },
    computed: {
        rootClasses() {
            return {
                'localized-date-picker--datetime': this.mode === 'datetime',
                'localized-date-picker--disabled': this.disabled,
                'localized-date-picker--open': this.isOpen,
                'localized-date-picker--clearable': this.canClear
            }
        },
        canClear() {
            return !this.disabled && Boolean(this.value || this.draftDate)
        },
        inputLocale() {
            const employee = this.$store.getters['loginUser/getUserInformation'] || {}
            const location = this.$store.getters['loginUser/getLocalLocation'] || {}
            return resolveDateDisplayLocale(this.$i18n.locale, this.$store.getters['login/getConfig'] || {}, employee.lang || employee.language, location.lang, this.$store.getters['intl/getLocale'])
        },
        inputPlaceholder() {
            return this.placeholder || dateInputPlaceholder(this.inputLocale, this.mode)
        },
        calendarLocale() {
            return dateInputCalendar(this.inputLocale)
        },
        displayedDate() {
            return this.isOpen && this.draftDate ? this.draftDate : this.selectedDate
        },
        displayedTime() {
            return this.isOpen ? this.draftTime : this.selectedTime
        },
        nativeInputValue() {
            if (this.inputText !== null) return this.inputText
            if (!this.displayedDate) {
                return ''
            }

            if (this.mode === 'datetime') {
                return formatDateInput(serializeNativeDateValue(this.displayedDate, this.displayedTime, this.mode), this.inputLocale, this.mode)
            }

            return formatDateInput(formatSelectedIso(this.displayedDate), this.inputLocale)
        },
        nativeMin() {
            return this.min ? this.min.slice(0, 10) : undefined
        },
        nativeMax() {
            return this.max ? this.max.slice(0, 10) : undefined
        },
        parsedValue() {
            return parseNativeDateValue(this.value)
        },
        selectedDate() {
            return this.parsedValue.date
        },
        selectedTime() {
            return this.parsedValue.time
        },
        dayNames() {
            return this.calendarLocale.dayNames
        },
        monthNames() {
            return this.calendarLocale.monthNames
        },
        pickerLimits() {
            if (!this.min && !this.max) {
                return false
            }

            return {
                min: this.min || '1900-01-01',
                max: this.max || '2100-12-31'
            }
        }
    },
    watch: {
        value() {
            this.resetInput()
        },
        inputLocale() {
            this.resetInput()
        }
    },
    methods: {
        resetInput() {
            this.inputText = null
            this.invalidInput = false
            if (this.$refs.nativeInput) this.$refs.nativeInput.setCustomValidity('')
        },
        onTextInput(event) {
            this.inputText = event.target.value
            this.invalidInput = false
            event.target.setCustomValidity('')
        },
        onInputKeydown(event) {
            if (event.key === 'Enter') {
                event.preventDefault()
                this.onNativeInputChange({ target: this.$refs.nativeInput })
            } else if (event.key === 'Escape') {
                this.resetInput()
                if (this.isOpen) this.closeCalendar()
            } else if (event.key === 'ArrowDown' && event.altKey) {
                event.preventDefault()
                this.openCalendar()
            }
        },
        onTriggerClick(event) {
            if (this.disabled || this.isClearControl(event.target)) {
                return
            }

            event.preventDefault()
            this.toggleCalendar()
        },
        onTriggerKeydown(event) {
            if (this.isClearControl(event.target)) {
                return
            }

            if (event.key === 'Escape' && this.isOpen) {
                event.preventDefault()
                this.closeCalendar()
                return
            }

            if ((event.key === 'Backspace' || event.key === 'Delete') && this.canClear) {
                event.preventDefault()
                this.clearValue()
                return
            }

            if (event.key === ' ' || event.key === 'Enter') {
                event.preventDefault()
                this.toggleCalendar()
            }
        },
        isClearControl(target) {
            return Boolean(target && target.closest && target.closest('.localized-date-picker__clear'))
        },
        onClearClick(event) {
            if (this.disabled) {
                return
            }

            event.preventDefault()
            event.stopPropagation()
            this.clearValue()
        },
        clearValue() {
            this.resetInput()
            this.lastSelectedIso = ''
            this.draftDate = null
            this.draftTime = ''

            if (this.isOpen) {
                this.clearRepositionTimer()
                this.detachDismissListeners()
                this.restoreOverlayToComponent()
                this.isOpen = false
            }

            this.$emit('input', '')
            this.$emit(
                'change',
                createNativeLikeChangeEvent('', () => {
                    this.lastSelectedIso = this.selectedDate ? formatSelectedIso(this.selectedDate) : ''
                })
            )
        },
        onNativeInputChange(event) {
            const typed = event.target.value || ''
            if (!typed) {
                this.clearValue()
                return
            }

            const iso = parseDateInput(typed, this.inputLocale, this.mode)
            if (!iso || !isDateInputInRange(iso, this.min, this.max)) {
                this.invalidInput = true
                event.target.setCustomValidity(this.inputPlaceholder)
                event.target.reportValidity()
                return
            }

            const parsed = parseNativeDateValue(iso)
            this.resetInput()
            this.lastSelectedIso = formatSelectedIso(parsed.date)
            this.draftDate = parsed.date
            this.draftTime = parsed.time || this.selectedTime
            this.emitNativeValue(serializeNativeDateValue(parsed.date, this.draftTime, this.mode))
        },
        patchNativeShowPicker() {
            const input = this.$refs.nativeInput
            if (!input || input._localizedShowPickerPatched) {
                return
            }

            input._localizedShowPickerPatched = true
            input.showPicker = () => {
                if (!this.disabled) {
                    this.openCalendar()
                }
            }
        },
        toggleCalendar() {
            if (this.disabled) {
                return
            }

            if (this.isOpen) {
                this.closeCalendar()
                return
            }

            this.openCalendar()
        },
        openCalendar() {
            this.resetInput()
            this.draftDate = this.selectedDate
            this.draftTime = this.selectedTime
            this.initialPickerDate = this.selectedDate || new Date()
            this.lastSelectedIso = this.selectedDate ? formatSelectedIso(this.selectedDate) : ''
            this.isOpen = true
            this.$nextTick(() => {
                this.ensurePortal()
                this.hydrateCalendar()
                this.positionPopover()
                this.attachDismissListeners()
                this.scheduleReposition()
            })
        },
        closeCalendar() {
            this.commitDatetimeIfNeeded()
            this.clearRepositionTimer()
            this.detachDismissListeners()
            this.restoreOverlayToComponent()
            this.isOpen = false
        },
        commitDatetimeIfNeeded() {
            if (this.mode !== 'datetime' || !this.draftDate) {
                return
            }

            this.emitNativeValue(serializeNativeDateValue(this.draftDate, this.draftTime, this.mode))
        },
        ensurePortal() {
            if (this.$refs.backdrop && this.$refs.backdrop.parentNode !== document.body) {
                document.body.appendChild(this.$refs.backdrop)
            }
            if (this.$refs.popover && this.$refs.popover.parentNode !== document.body) {
                document.body.appendChild(this.$refs.popover)
            }
        },
        restoreOverlayToComponent() {
            const root = this.$el
            if (!root) {
                return
            }

            if (this.$refs.backdrop && this.$refs.backdrop.parentNode !== root) {
                root.appendChild(this.$refs.backdrop)
            }
            if (this.$refs.popover && this.$refs.popover.parentNode !== root) {
                root.appendChild(this.$refs.popover)
            }
        },
        hydrateCalendar() {
            const calendar = this.$refs.calendar
            if (!calendar || !calendar.calendar || !this.selectedDate) {
                return
            }

            calendar.calendar.selectedDate = moment(this.selectedDate).format('YYYY-M-D')
            calendar.calendar.currentDate = this.selectedDate

            if (typeof calendar.markChooseDays === 'function') {
                calendar.markChooseDays()
            }

            this.preventLibraryHeightPadding()
        },
        preventLibraryHeightPadding() {
            const calendar = this.$refs.calendar
            if (!calendar) {
                return
            }

            const container = calendar.$refs && calendar.$refs.mainContainer
            if (container) {
                container.style.height = 'auto'
            }

            if (typeof calendar.setCalendarStyles !== 'function' || calendar._localizedHeightPatched) {
                return
            }

            calendar._localizedHeightPatched = true
            calendar.setCalendarStyles = () => {
                if (calendar.$refs && calendar.$refs.mainContainer) {
                    calendar.$refs.mainContainer.style.height = 'auto'
                }
            }
        },
        scheduleReposition() {
            this.clearRepositionTimer()
            this.repositionTimer = setTimeout(() => {
                this.ensurePortal()
                this.positionPopover()
            }, 80)
        },
        clearRepositionTimer() {
            if (this.repositionTimer) {
                clearTimeout(this.repositionTimer)
                this.repositionTimer = null
            }
        },
        positionPopover() {
            const anchor = this.$refs.anchor || this.$el
            const popover = this.$refs.popover
            if (!anchor || !popover || !this.isOpen) {
                return
            }

            const rect = anchor.getBoundingClientRect()
            const popoverWidth = Math.min(POPOVER_WIDTH, window.innerWidth - POPOVER_GAP * 2)
            const spaceBelow = window.innerHeight - rect.bottom - POPOVER_GAP
            const spaceAbove = rect.top - POPOVER_GAP
            const openUpward = spaceBelow < POPOVER_MIN_HEIGHT && spaceAbove > spaceBelow

            let left = rect.right - popoverWidth
            left = Math.min(Math.max(POPOVER_GAP, left), window.innerWidth - popoverWidth - POPOVER_GAP)

            popover.style.position = 'fixed'
            popover.style.left = left + 'px'
            popover.style.width = popoverWidth + 'px'
            popover.style.zIndex = '10000'
            popover.style.visibility = 'visible'

            if (openUpward) {
                popover.style.top = 'auto'
                popover.style.bottom = window.innerHeight - rect.top + POPOVER_GAP + 'px'
                popover.style.maxHeight = Math.max(spaceAbove, 0) + 'px'
            } else {
                popover.style.bottom = 'auto'
                popover.style.top = rect.bottom + POPOVER_GAP + 'px'
                popover.style.maxHeight = Math.max(spaceBelow, 0) + 'px'
            }
        },
        attachDismissListeners() {
            window.addEventListener('resize', this.positionPopover)
            window.addEventListener('scroll', this.positionPopover, true)
        },
        detachDismissListeners() {
            window.removeEventListener('resize', this.positionPopover)
            window.removeEventListener('scroll', this.positionPopover, true)
        },
        onCalendarInput(calendar) {
            const selected = calendar && calendar.selectedDate ? calendar.selectedDate : ''
            const parsed = parseCalendarDate(selected)
            if (!parsed) {
                return
            }

            const nextIso = formatSelectedIso(parsed)
            if (nextIso === this.lastSelectedIso) {
                return
            }

            this.lastSelectedIso = nextIso
            this.draftDate = parsed

            if (this.mode === 'datetime') {
                return
            }

            this.emitNativeValue(serializeNativeDateValue(parsed, this.selectedTime, this.mode))
            this.closeCalendar()
        },
        onTimeChanged(event) {
            if (this.disabled) {
                return
            }

            this.draftTime = event.target.value
            const date = this.draftDate || this.selectedDate || new Date()
            this.draftDate = date
            this.emitNativeValue(serializeNativeDateValue(date, this.draftTime, this.mode))
        },
        emitNativeValue(nextValue) {
            if (nextValue === this.value) {
                return
            }

            this.$emit('input', nextValue)
            this.$emit(
                'change',
                createNativeLikeChangeEvent(nextValue, () => {
                    this.lastSelectedIso = this.selectedDate ? formatSelectedIso(this.selectedDate) : ''
                })
            )
        }
    },
    mounted() {
        this.patchNativeShowPicker()
    },
    updated() {
        this.patchNativeShowPicker()
        if (this.isOpen) {
            this.ensurePortal()
            this.positionPopover()
        }
    },
    beforeDestroy() {
        this.clearRepositionTimer()
        this.detachDismissListeners()
        this.restoreOverlayToComponent()
    }
}

/**
 * @param {Date} date
 * @returns {string}
 */
function formatSelectedIso(date) {
    return moment(date).format('YYYY-MM-DD')
}
</script>

<style lang="scss">
.localized-date-picker {
    position: relative;
    box-sizing: border-box;
    display: flex;
    flex-wrap: nowrap;
    align-items: center;
    cursor: pointer;
    outline: none;
    padding-left: 44px;
    background-repeat: no-repeat;
    background-size: 28px;
    background-position: left 8px center;
    background-image: img('input_date_cccccc.svg');

    &--datetime {
        background-image: img('input_datetime_cccccc.svg');
    }

    &--disabled {
        pointer-events: none;
        opacity: 0.7;
    }

    &--clearable {
        padding-right: 24px;
    }

    &__clear {
        position: absolute;
        top: 50%;
        right: 6px;
        z-index: 1;
        width: 16px;
        height: 16px;
        margin: 0;
        padding: 0;
        border: 0;
        background: none;
        box-shadow: none;
        transform: translateY(-50%);
        cursor: pointer;
        pointer-events: auto;
        color: #767676;
        line-height: 16px;
        text-align: center;
        font-size: 18px;
        font-weight: 400;

        &::before {
            content: '\00d7';
        }

        &:hover,
        &:focus,
        &:active {
            background: none;
            background-color: transparent;
            box-shadow: none;
            color: #333;
        }
    }

    &__input {
        display: block;
        flex: 1 1 auto;
        width: auto;
        min-width: 0;
        max-width: 100%;
        height: 100%;
        margin: 0;
        padding: 0;
        border: none;
        color: inherit;
        font: inherit;
        text-align: left;
        cursor: text;
        outline: none;
        box-shadow: none;
        background-color: transparent;
        background-image: none;
        appearance: none;
        -webkit-appearance: none;
        pointer-events: auto;

        &:hover,
        &:focus,
        &:active {
            background-color: transparent;
            background-image: none;
            color: inherit;
            outline: none;
            box-shadow: none;
            border: none;
        }

        &::-webkit-datetime-edit,
        &::-webkit-datetime-edit-fields-wrapper {
            padding: 0;
            margin: 0;
            background: transparent;
        }

        &::-webkit-calendar-picker-indicator {
            display: none;
            width: 0;
            padding: 0;
            margin: 0;
        }
    }

    &__backdrop {
        position: fixed;
        inset: 0;
        z-index: 9998;
        background: transparent;
    }

    &__popover {
        .vfc-dayNames {
            text-transform: none;

            span {
                text-transform: none;
            }
        }

        position: fixed;
        z-index: 10000;
        visibility: hidden;
        overflow: auto;
        background: #fff;
        border: 1px solid $neutro;
        border-radius: 8px;
        box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
        padding: 8px 8px 4px;
        box-sizing: border-box;

        .vfc-main-container {
            width: 100%;
            height: auto !important;
            min-height: 0;
            padding: 4px 8px 0;
            border-radius: 8px;
            box-shadow: none;
        }

        .vfc-calendars,
        .vfc-calendar,
        .vfc-content {
            width: 100%;
            height: auto;
            background: #fff;
        }

        .vfc-main-container {
            .vfc-calendars-container .vfc-navigation-buttons {
                display: flex;
                align-items: center;
                top: 8px;
                height: 32px;
                margin-top: 0;
                padding: 0 8px;
                box-sizing: border-box;

                > div,
                .vfc-cursor-pointer {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    position: relative;
                    top: 0;
                    width: 32px;
                    height: 32px;
                    margin: 0;
                    background-color: $neutro-t70;
                    border-radius: 4px;
                    box-sizing: border-box;

                    .vfc-arrow-left,
                    .vfc-arrow-right,
                    .vfc-disabled {
                        position: static;
                        top: auto;
                        right: auto;
                        left: auto;
                        width: 10px;
                        height: 10px;
                        margin: 0;
                        box-sizing: border-box;
                    }

                    .vfc-arrow-left {
                        transform: rotate(-45deg);
                    }

                    .vfc-arrow-right {
                        transform: rotate(45deg);
                    }
                }
            }

            .vfc-calendars .vfc-content {
                h2.vfc-top-date,
                h2.vfc-top-date.vfc-underline,
                h2.vfc-top-date.vfc-cursor-pointer.vfc-underline {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    box-sizing: border-box;
                    height: 32px;
                    margin: 8px 0 !important;
                    padding: 0 !important;
                    font-family: $text-sbold;
                    font-size: 16px;
                    color: $main;
                    cursor: pointer;
                    word-spacing: 4px;
                    position: relative;
                    top: 0 !important;
                    line-height: 1;
                }
            }
        }

        .vfc-marked {
            background-color: $main-dark;
            color: #fff;
        }

        .vfc-months .vfc-item,
        .vfc-years .vfc-item {
            cursor: pointer;
        }

        .vfc-selected {
            background-color: $main-dark;
            color: #fff;
        }
    }

    &__time {
        display: block;
        width: 100%;
        margin-top: 4px;
        height: 36px;
        padding: 0 8px;
        border: 1px solid $neutro;
        border-radius: 4px;
        background: #fff;
        color: inherit;
        font: inherit;
    }
}
</style>
